import { FC, useEffect, useState } from "react";
import { Score } from "@/models/pdfData";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  PaperOwnProps,
  TextField,
  Typography,
} from "@mui/material";
import { useGameDataContext } from "@context/GameDataContext";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";

const TeamIcon: FC<{ src: string }> = ({ src }) => (
  <Box
    maxWidth={"100px"}
    sx={{
      objectFit: "contain",
      boxShadow: "black",
      filter: "drop-shadow(0px 0px 4px #0002)",
    }}
  >
    <img
      alt="Team Icon"
      src={src}
      style={{ maxWidth: "100%", height: "auto" }}
    />
  </Box>
);

const ScoreSelector: FC<{
  title: string;
  setScore: (score: Score) => void;
  score?: Score;
  disabled?: boolean;
  greyedOut?: boolean;
}> = ({ score, setScore, title, disabled = false, greyedOut = false }) => {
  const {
    challenge: { challenger },
  } = useSelectedChallengeContext();
  const { game } = useGameDataContext();

  const team1 = game?.gameData?.team1 || { iconUrl: null, clubCode: "A" };
  const team2 = game?.gameData?.team2 || { iconUrl: null, clubCode: "B" };

  const firstTeam = challenger === "team1" ? team1 : team2;
  const secondTeam = challenger === "team1" ? team2 : team1;

  const firstTeamPropScore =
    score?.[challenger === "team1" ? "team1" : "team2"];
  const secondTeamPropScore =
    score?.[challenger === "team1" ? "team2" : "team1"];

  const [localFirst, setLocalFirst] = useState<number | "">(
    firstTeamPropScore ?? "",
  );
  const [localSecond, setLocalSecond] = useState<number | "">(
    secondTeamPropScore ?? "",
  );
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalFirst(firstTeamPropScore ?? "");
      setLocalSecond(secondTeamPropScore ?? "");
    }
  }, [firstTeamPropScore, secondTeamPropScore, isFocused]);

  const pushToParent = (newFirst: number | "", newSecond: number | "") => {
    if (newFirst !== "" && newSecond !== "") {
      setScore({
        team1: challenger === "team1" ? newFirst : newSecond,
        team2: challenger === "team1" ? newSecond : newFirst,
      });
    }
  };

  const opacity = greyedOut ? 0.2 : disabled ? 0.6 : 1;
  const variant: PaperOwnProps["variant"] = greyedOut
    ? "outlined"
    : "elevation";
  const elevation: PaperOwnProps["elevation"] =
    greyedOut || disabled ? undefined : 5;

  return (
    <Card
      variant={variant}
      sx={{ width: "350px", mx: 2 }}
      elevation={elevation}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsFocused(false);
        }
      }}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: "subtitle1", align: "center" }}
        sx={{ pb: 0, opacity }}
      />
      <CardContent>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={1}
        >
          <Box flexShrink={1} display={"flex"}>
            {firstTeam.iconUrl ? (
              <TeamIcon src={firstTeam.iconUrl} />
            ) : (
              <Typography>{firstTeam.clubCode}</Typography>
            )}
          </Box>
          <Box flexShrink={1} minWidth={"50px"}>
            <TextField
              id="team1Score"
              label={firstTeam.clubCode}
              variant="outlined"
              type="text"
              autoComplete="off"
              value={!greyedOut ? localFirst : ""}
              onChange={(event) => {
                const valStr = /\d{0,2}/.exec(event.target.value)?.[0];
                const val = valStr ? Number.parseInt(valStr, 10) : "";
                setLocalFirst(val);
                pushToParent(val, localSecond);
              }}
              disabled={disabled || greyedOut}
            />
          </Box>
          <Box flexShrink={0}>
            <Typography align={"center"}>:</Typography>
          </Box>
          <Box flexShrink={1} minWidth={"50px"}>
            <TextField
              id="team2Score"
              label={secondTeam.clubCode}
              variant="outlined"
              type="text"
              autoComplete="off"
              value={!greyedOut ? localSecond : ""}
              onChange={(event) => {
                const valStr = /\d{0,2}/.exec(event.target.value)?.[0];
                const val = valStr ? Number.parseInt(valStr, 10) : "";
                setLocalSecond(val);
                pushToParent(localFirst, val);
              }}
              disabled={disabled || greyedOut}
            />
          </Box>
          <Box flexShrink={1} display={"flex"}>
            {secondTeam.iconUrl ? (
              <TeamIcon src={secondTeam.iconUrl} />
            ) : (
              <Typography>{secondTeam.clubCode}</Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScoreSelector;
